import { BaseGrid } from './BaseGrid';
import { Cell } from './Cell';
import { GridType } from './GridType';

export interface SquareGridConfig {
  rows: number;
  cols: number;
  allowDiagonal?: boolean;
}

export class SquareGrid extends BaseGrid {
  public allowDiagonal: boolean;

  constructor(config: SquareGridConfig) {
    super({
      rows: config.rows,
      cols: config.cols,
      gridType: GridType.SQUARE
    });
    this.allowDiagonal = config.allowDiagonal ?? true;
  }

  getNeighbors(cell: Cell): Cell[] {
    const neighbors: Cell[] = [];
    const directions = this.allowDiagonal
      ? [
          [-1, 0], [1, 0], [0, -1], [0, 1],
          [-1, -1], [-1, 1], [1, -1], [1, 1]
        ]
      : [[-1, 0], [1, 0], [0, -1], [0, 1]];

    for (const [dRow, dCol] of directions) {
      const newRow = cell.row + dRow;
      const newCol = cell.col + dCol;
      const neighbor = this.getCell(newRow, newCol);

      if (neighbor && neighbor.isWalkable) {
        if (this.allowDiagonal && Math.abs(dRow) === 1 && Math.abs(dCol) === 1) {
          const horizontalCell = this.getCell(cell.row, newCol);
          const verticalCell = this.getCell(newRow, cell.col);
          if (horizontalCell?.isWalkable || verticalCell?.isWalkable) {
            neighbors.push(neighbor);
          }
        } else {
          neighbors.push(neighbor);
        }
      }
    }

    return neighbors;
  }

  calculateDistance(cellA: Cell, cellB: Cell): number {
    const dx = Math.abs(cellA.row - cellB.row);
    const dy = Math.abs(cellA.col - cellB.col);

    if (!this.allowDiagonal) {
      return dx + dy;
    }

    if (dx > dy) {
      return Math.SQRT2 * dy + (dx - dy);
    }
    return Math.SQRT2 * dx + (dy - dx);
  }

  clone(): SquareGrid {
    const newGrid = new SquareGrid({
      rows: this.rows,
      cols: this.cols,
      allowDiagonal: this.allowDiagonal
    });

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const originalCell = this.cells[row][col];
        const newCell = newGrid.getCellAt(row, col);
        newCell.type = originalCell.type;
        newCell.weight = originalCell.weight;
      }
    }

    return newGrid;
  }
}
